import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface ComparisonCardProps {
  label: string;
  currentValue: number;
  previousValue: number;
  changePercent: number;
  format?: 'currency' | 'number' | 'percentage';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Individual KPI comparison card showing current vs previous period
 * Following best practices from Power BI, Tableau, and Google Analytics
 */
export default function ComparisonCard({
  label,
  currentValue,
  previousValue,
  changePercent,
  format = 'number',
  size = 'md',
}: ComparisonCardProps) {
  const isPositive = changePercent > 0.5;
  const isNegative = changePercent < -0.5;
  const isNeutral = !isPositive && !isNegative;

  // Format value based on type
  const formatValue = (value: number) => {
    if (format === 'currency') {
      if (value >= 1000000) {
        return `฿${(value / 1000000).toFixed(1)}M`;
      }
      if (value >= 1000) {
        return `฿${(value / 1000).toFixed(1)}K`;
      }
      return `฿${value.toLocaleString()}`;
    }
    if (format === 'percentage') {
      return `${value.toFixed(1)}%`;
    }
    // Number format
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  // Size configurations
  const sizeConfig = {
    sm: {
      padding: 'p-3',
      labelSize: 'text-xs',
      valueSize: 'text-lg',
      prevSize: 'text-xs',
      changeSize: 'text-xs',
      iconSize: 'h-3 w-3',
    },
    md: {
      padding: 'p-4',
      labelSize: 'text-sm',
      valueSize: 'text-2xl',
      prevSize: 'text-sm',
      changeSize: 'text-sm',
      iconSize: 'h-4 w-4',
    },
    lg: {
      padding: 'p-5',
      labelSize: 'text-base',
      valueSize: 'text-3xl',
      prevSize: 'text-base',
      changeSize: 'text-base',
      iconSize: 'h-5 w-5',
    },
  };

  const config = sizeConfig[size];

  return (
    <div className={`${config.padding} rounded-lg border bg-card`}>
      {/* Label */}
      <div className={`${config.labelSize} font-medium text-muted-foreground mb-1`}>
        {label}
      </div>

      {/* Current Value */}
      <div className={`${config.valueSize} font-bold text-foreground mb-2`}>
        {formatValue(currentValue)}
      </div>

      {/* Comparison Row */}
      <div className="flex items-center justify-between">
        {/* Previous Value */}
        <div className={`${config.prevSize} text-muted-foreground`}>
          vs {formatValue(previousValue)}
        </div>

        {/* Change Indicator */}
        <div
          className={`flex items-center gap-1 ${config.changeSize} font-medium ${
            isPositive
              ? 'text-green-600 dark:text-green-400'
              : isNegative
              ? 'text-red-600 dark:text-red-400'
              : 'text-muted-foreground'
          }`}
        >
          {isPositive && <TrendingUp className={config.iconSize} />}
          {isNegative && <TrendingDown className={config.iconSize} />}
          {isNeutral && <Minus className={config.iconSize} />}
          <span>
            {isPositive && '+'}
            {changePercent.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Mini progress bar showing relative change */}
      <div className="mt-3 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            isPositive
              ? 'bg-green-500'
              : isNegative
              ? 'bg-red-500'
              : 'bg-muted-foreground'
          }`}
          style={{
            width: `${Math.min(Math.abs(changePercent) + 50, 100)}%`,
          }}
        />
      </div>
    </div>
  );
}
